import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Button,
  Alert,
} from "reactstrap";
import {
  WhatsappIcon,
  FacebookIcon,
  // XIcon,
  EmailIcon,
  // LinkedinIcon,
  TelegramIcon,
} from "react-share";
import Icon from "Components/Icons/Icon";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { sendInvoiceEmail, sendQuoteEmail } from "slices/thunks";
import { QuoteDetailsPayload } from "types/quote.api";
import { InvoiceDetailsPayload } from "types/invoice.api";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfTitle?: string;
  pdfUrl?: string;
  route_url?: string;
  isInvoice: boolean;
  quoteDetails?: QuoteDetailsPayload | null;
  invoiceDetails?: InvoiceDetailsPayload | null;
  isShareFromQuote?: boolean;
}

const ShareModal = ({
  isOpen,
  onClose,
  pdfTitle = "Check out this PDF",
  pdfUrl,
  route_url,
  isInvoice,
  quoteDetails,
  invoiceDetails,
  isShareFromQuote,
}: ShareModalProps) => {
  const [emailStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const finalUrl = pdfUrl ? `${pdfUrl}?download=1` : "";
  // const message = `Hi, please find your ${
  //   isShareFromQuote ? "quote" : "invoice"
  // } attached. You can view or download it here: ${finalUrl}`;
  const message = `Hi, please find your ${
    isShareFromQuote ? "quote" : "invoice"
  } here:

View: ${route_url}

Download: ${finalUrl}

Thank you.`;
  const canUseTelegram = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const dispatch = useAppDispatch();
  const { status: invoiceStatus } = useAppSelector((state) => state.invoice);
  const { status: quoteStatus } = useAppSelector((state) => state.quote);

  const isSending = isInvoice
    ? invoiceStatus.sendEmail === "loading"
    : quoteStatus.sendEmail === "loading";

  // Handle native share with actual file
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          // title: "Quote",
          text: message,
          // url: quoteDetails?.url,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      // Fallback
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
          "Quote",
        )}&body=${encodeURIComponent(message + "\n\n" + quoteDetails?.url)}`,
        "_blank",
      );
    }
  };

  const handleEmailShare = async () => {
    if (isInvoice) {
      dispatch(sendInvoiceEmail({ invoiceId: Number(invoiceDetails?.id) }));
    } else {
      dispatch(sendQuoteEmail({ quoteId: Number(quoteDetails?.id) }));
    }
  };

  const handleTelegramShare = () => {
    const url = encodeURIComponent(finalUrl);
    const text = encodeURIComponent(message);

    const tgAppUrl = `tg://msg_url?url=${url}&text=${text}`;
    const tgWebUrl = `https://t.me/share/url?url=${url}&text=${text}`;

    // Try Telegram App first
    window.location.href = tgAppUrl;

    // Fallback to Telegram Web
    setTimeout(() => {
      window.open(tgWebUrl, "_blank");
    }, 700);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="md"
      contentClassName="br-7"
    >
      <ModalHeader
        toggle={onClose}
        className="fw-medium fs-16 text-black2D p-3 bg-greyF5"
      >
        Share PDF
      </ModalHeader>

      <ModalBody className="p-3">
        {/* Status Alert */}
        {emailStatus && (
          <Alert
            color={emailStatus.type === "success" ? "success" : "danger"}
            className="mb-3"
          >
            {emailStatus.message}
          </Alert>
        )}

        {/* Share Icons - All trigger native share with file */}
        <div className="mb-3">
          <div
            className="d-flex gap-3 pb-2 justify-content-evenly"
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              paddingBottom: "8px",
              scrollbarWidth: "thin",
            }}
          >
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                message,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                className="text-center cursor-pointer"
                style={{ cursor: "pointer", minWidth: "56px" }}
              >
                <WhatsappIcon size={56} round />
                <div className="fs-12 mt-1">WhatsApp</div>
              </div>
            </a>

            <a
              href={`https://www.facebook.com/dialog/send?app_id=YOUR_APP_ID&link=${encodeURIComponent(
                finalUrl,
              )}&redirect_uri=${encodeURIComponent(finalUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                className="text-center cursor-pointer"
                style={{ cursor: "pointer", minWidth: "56px" }}
              >
                <FacebookIcon size={56} round />
                <div className="fs-12 mt-1">Facebook</div>
              </div>
            </a>

            {/* <div
              className="text-center cursor-pointer"
              onClick={() => {
                const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  message
                )}&url=${encodeURIComponent(finalUrl)}`;
                window.open(shareUrl, "_blank");
              }}
              style={{ cursor: "pointer", minWidth: "56px" }}
            >
              <XIcon size={56} round />
              <div className="fs-12 mt-1">X</div>
            </div> */}

            <a
              href={`mailto:?subject=${encodeURIComponent(
                pdfTitle,
              )}&body=${encodeURIComponent(message)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                className="text-center cursor-pointer"
                style={{ minWidth: "56px" }}
              >
                <EmailIcon size={56} round />
                <div className="fs-12 mt-1">Email</div>
              </div>
            </a>

            {/* <div
              className="text-center cursor-pointer"
              onClick={() => {
                const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  finalUrl
                )}&text=${encodeURIComponent(message)}`;
                window.open(shareUrl, "_blank");
              }}
              style={{ cursor: "pointer", minWidth: "56px" }}
            >
              <LinkedinIcon size={56} round />
              <div className="fs-12 mt-1">LinkedIn</div>
            </div> */}

            {canUseTelegram && (
              <div
                className="text-center cursor-pointer"
                onClick={handleTelegramShare}
                style={{ cursor: "pointer", minWidth: "56px" }}
              >
                <TelegramIcon size={56} round />
                <div className="fs-12 mt-1">Telegram</div>
              </div>
            )}

            <div className=" d-flex flex-column align-items-center justify-content-center">
              <div
                className="text-center cursor-pointer bg-greyF5 rounded-circle d-flex flex-column align-items-center justify-content-center"
                style={{
                  width: 56,
                  height: 56,
                  minWidth: "56px",
                  flexShrink: 0,
                }}
                onClick={handleNativeShare}
              >
                <Icon name="ShareMore" width={24} height={24} />
              </div>
              <div className="fs-12 mt-1" style={{ whiteSpace: "nowrap" }}>
                More
              </div>
            </div>
          </div>
        </div>

        {/* Email Input */}
        <div className="mb-3">
          <div className="email-share-wrapper d-flex align-items-center p-0 m-0 pe-1">
            <Input
              type="email"
              placeholder="Enter email address"
              value={
                isInvoice
                  ? invoiceDetails?.client.email
                  : quoteDetails?.client.email || ""
              }
              disabled
              onKeyPress={(e) => e.key === "Enter" && handleEmailShare()}
              className="email-input flex-grow-1"
            />

            <Button
              color="primary"
              onClick={handleEmailShare}
              disabled={isSending}
              className="email-send-btn ms-2"
            >
              {isSending ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default React.memo(ShareModal);
